import { getAuthHeaders, getBaseUrl } from './apiClient';

export type JobStatus =
  | 'queued'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export type JobRecord = {
  job_id: string;
  status: JobStatus;
  job_type?: string;
  attempts?: number;
  max_attempts?: number;
  submitted_at?: string;
  started_at?: string;
  finished_at?: string;
  last_heartbeat?: string;
  last_progress_event?: string;
  updated_at?: string;
  cancel_requested?: boolean;
  phase?: string;
  progress?: any[];
  result_inline?: any;
  error?: any;
  envelope?: any;
  user_email?: string;
};

export const jobsService = {
  submit: async (job_type: string, payload: Record<string, any>, extra?: { client?: string; max_attempts?: number }) => {
    const baseUrl = getBaseUrl();
    const headers = await getAuthHeaders();
    const resp = await fetch(`${baseUrl}/secure/jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        job_type,
        payload,
        client: extra?.client,
        max_attempts: extra?.max_attempts,
      }),
    });
    const raw = await resp.text().catch(() => '');
    let data: any = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!resp.ok) {
      throw new Error(data?.error || raw || `Failed to submit job (${resp.status})`);
    }
    if (!data?.job_id) {
      throw new Error('Job submission returned no job_id');
    }
    return data as { job_id: string; status: JobStatus };
  },

  get: async (jobId: string): Promise<JobRecord> => {
    const baseUrl = getBaseUrl();
    const headers = await getAuthHeaders();
    const resp = await fetch(`${baseUrl}/secure/jobs/${encodeURIComponent(jobId)}`, {
      method: 'GET',
      headers,
    });
    const raw = await resp.text().catch(() => '');
    let data: any = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!resp.ok) {
      throw new Error(data?.error || raw || `Failed to fetch job (${resp.status})`);
    }
    return data as JobRecord;
  },

  waitForCompletion: async (
    jobId: string,
    options?: { intervalMs?: number; timeoutMs?: number; onUpdate?: (job: JobRecord) => void }
  ): Promise<JobRecord> => {
    const intervalMs = options?.intervalMs ?? 1500;
    const timeoutMs = options?.timeoutMs ?? 10 * 60 * 1000; // 10 minutes
    const start = Date.now();

    while (true) {
      const job = await jobsService.get(jobId);
      try { options?.onUpdate?.(job); } catch {}
      if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
        return job;
      }
      if (Date.now() - start > timeoutMs) {
        throw new Error(`Job polling timed out after ${Math.round(timeoutMs / 1000)}s (job_id=${jobId})`);
      }
      await new Promise(r => setTimeout(r, intervalMs));
    }
  },

  cancel: async (jobId: string): Promise<any> => {
    const baseUrl = getBaseUrl();
    const headers = await getAuthHeaders();
    const resp = await fetch(`${baseUrl}/secure/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
    const raw = await resp.text().catch(() => '');
    let data: any = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!resp.ok) {
      throw new Error(data?.error || raw || `Failed to cancel job (${resp.status})`);
    }
    return data;
  },

  diagnose: async (): Promise<any> => {
    const baseUrl = getBaseUrl();
    const headers = await getAuthHeaders();
    const resp = await fetch(`${baseUrl}/secure/jobs/diagnose`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
    const raw = await resp.text().catch(() => '');
    let data: any = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!resp.ok) {
      throw new Error(data?.error || raw || `Failed to run diagnostics (${resp.status})`);
    }
    return data;
  },

  // Convenience: run a TalkToTally tool in the background (safe for long LLM calls)
  submitTallyTool: async (
    tool: string,
    toolVersion: string,
    args: Record<string, any>,
    context?: Record<string, any>
  ) => {
    return jobsService.submit(
      'tally_tool_execute',
      { tool, tool_version: toolVersion, args, context: context || {} },
      { client: 'web' }
    );
  },
};


